/**
 * What colour a place's header is: calm unless something is happening there.
 * Plain TypeScript; `ForecastHero` puts the answer on
 * `data-tone`, and `app.css` maps each tone to a field and its ink.
 *
 * The red that used to fill every header read as "something is wrong here"
 * on a quiet afternoon (user feedback, 2026-09-29), so colour now carries
 * information and only that. An alert outranks the weather: a Watch on a
 * rainy day is yellow, not blue.
 */

import type { Current, ForecastBundle, Glance } from '../api/types'

export type AlertTone = 'watch' | 'warning' | 'emergency'
export type WeatherTone = 'calm' | 'rain' | 'snow' | 'storm'
export type Tone = WeatherTone | AlertTone

/** Lowest first: the header takes the highest that applies. */
export const TONES: readonly Tone[] = ['calm', 'rain', 'snow', 'storm', 'watch', 'warning', 'emergency']

/**
 * NWS writes a Tornado Emergency and a Flash Flood Emergency under the
 * *Warning* event name and says "emergency" only in the text, as a tag line
 * ("...TORNADO EMERGENCY FOR MOORE..."). Upper case on purpose: the tag is
 * shouted, and "an emergency" in ordinary prose is not the tag.
 */
const EMERGENCY_TAG = /\b(TORNADO|FLASH FLOOD) EMERGENCY\b/

/**
 * An alert's tone by the event name's last word, the rule `alertLevel` and the
 * push poller follow, never NWS's `severity`. Anything short of a Warning —
 * Watch, Advisory, Statement — is the yellow tier.
 */
export function alertTone(event: string | null | undefined, text?: string | null): AlertTone {
  const last = (event ?? '').trim().split(/\s+/).at(-1) ?? ''
  if (/^emergency$/i.test(last) || (text && EMERGENCY_TAG.test(text))) return 'emergency'
  if (/^warning$/i.test(last)) return 'warning'
  return 'watch'
}

/**
 * The observation's own weather, from NWS's icon codes and, where the icon
 * says nothing, its text. An observation icon is one code
 * (`/icons/land/day/tsra?size=medium`); a forecast one can be two with
 * percentages (`/tsra,80/rain,40`), and every code in it counts.
 */
export function weatherTone(current: Pick<Current, 'icon' | 'description'> | null | undefined): WeatherTone {
  if (!current) return 'calm'
  return higher(fromIcon(current.icon), fromText(current.description))
}

const STORM_CODES = /^(tsra|tsra_sct|tsra_hi|tornado|hurricane|tropical_storm)$/
const SNOW_CODES = /^(snow|rain_snow|rain_sleet|snow_sleet|fzra|rain_fzra|snow_fzra|sleet|blizzard)$/
const RAIN_CODES = /^(rain|rain_showers|rain_showers_hi)$/

function fromIcon(icon: string | null): WeatherTone {
  if (!icon) return 'calm'
  const path = icon.split('?')[0]
  const at = path.search(/\/(day|night)\//)
  if (at < 0) return 'calm'
  let tone: WeatherTone = 'calm'
  for (const segment of path.slice(at).split('/').slice(2)) {
    const code = segment.split(',')[0]
    if (STORM_CODES.test(code)) tone = higher(tone, 'storm')
    else if (SNOW_CODES.test(code)) tone = higher(tone, 'snow')
    else if (RAIN_CODES.test(code)) tone = higher(tone, 'rain')
  }
  return tone
}

function fromText(text: string | null): WeatherTone {
  if (!text) return 'calm'
  if (/thunder|t-?storm/i.test(text)) return 'storm'
  // "Freezing Fog" is fog; freezing rain and drizzle are ice.
  if (/snow|sleet|ice pellets|blizzard|graupel|freezing (rain|drizzle)/i.test(text)) return 'snow'
  if (/rain|drizzle|showers/i.test(text)) return 'rain'
  return 'calm'
}

function higher<T extends Tone>(a: T, b: T): T {
  return TONES.indexOf(a) >= TONES.indexOf(b) ? a : b
}

/** The highest of the alerts in force, or the weather when there are none. */
export function conditionTone(bundle: Pick<ForecastBundle, 'current' | 'alerts'> | null | undefined): Tone {
  if (!bundle) return 'calm'
  let tone: Tone = weatherTone(bundle.current)
  for (const alert of bundle.alerts) {
    tone = higher(tone, alertTone(alert.event, alert.description))
  }
  return tone
}

/**
 * A Places row's block: the same rule as the header, from a glance's
 * observation and its alerts' event and emergency tag.
 */
export function glanceTone(glance: Pick<Glance, 'current' | 'alerts'>): Tone {
  let tone: Tone = weatherTone(glance.current)
  for (const alert of glance.alerts) tone = higher(tone, alertTone(alert.event, alert.tag))
  return tone
}

/** The alert that sets the tone: the highest tier, the first of equals. */
export function leadAlert<T extends { event: string | null; tag?: string | null }>(alerts: readonly T[]): T | null {
  let best: T | null = null
  for (const alert of alerts) {
    if (!best || TONES.indexOf(alertTone(alert.event, alert.tag)) > TONES.indexOf(alertTone(best.event, best.tag))) {
      best = alert
    }
  }
  return best
}
